import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import Warning from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { RateType } from '@treadinc/horizon-api-spec';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';
import { useMemo } from 'react';

import { Chip } from '~components/Chip/Chip';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import PricingHistoryButton from '~components/PricingHistory/PricingHistoryButton';
import SignaturesViewer from '~components/Signatures/SignaturesViewer';
import { FeatureFlags } from '~constants/featureFlags';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';
import { Job } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { Order } from '~hooks/useOrders/models';
import { JobLoadSignature } from '~hooks/useSignatures/models';
import { Ticket, TicketMetadata } from '~hooks/useTickets/models';
import { getDismissedDuplicateLabel } from '~pages/Approvals/ApprovalsComponents/DuplicateTicketIndicator';
import {
  DismissalDetails,
  DismissedBanner,
  OutlinedActionButton,
  OutlinedBadge,
} from '~pages/Approvals/Files/components/LoadsTable/components/AiReviewIndicator';
import { useStores } from '~store';
import { dateFormat } from '~utils/dateTime';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import useShouldShowHaulerRateCard from '~utils/hooks/useShouldShowHaulerRateCard';
import { extractRateInfoFromJob } from '~utils/jobs/job-utils';
import { userCompanyOwnsOrder } from '~utils/orders';
import { numberToIntegerOrDecimal } from '~utils/utilFunctions';

import { FlexColumn } from '../styled';
import { TicketDetailsBox } from './styled';
import TicketMetadataSummary from './TicketMetadataSummary';

const NO_CONTENT_INDICATOR = '-';
const TIMESTAMP_FORMAT = 'h:mm A [·] MMM D, YYYY';
const STAT_LABEL_FONT_SIZE = '10px';
const STAT_VALUE_FONT_SIZE = '16px';
const STAT_SUBTEXT_FONT_SIZE = '11px';
// Larger than any theme variant offers: h5 is 22px and h4 jumps to 32px at weight 400.
const TICKET_NUMBER_FONT_SIZE = '28px';

interface DigitalTicketViewProps {
  isAnalyzingTicket?: boolean;
  isChangingPotentialDuplicate?: boolean;
  isLoadingMetadata?: boolean;
  job: Job;
  load: Load;
  metadata?: TicketMetadata;
  onAddNewTicketClick?: () => void;
  onPotentialDuplicateChange?: (potentialDuplicate: boolean) => Promise<void>;
  order?: Order;
  ticket?: Ticket;
  signatures?: JobLoadSignature[];
}

export default function DigitalTicketView({
  isAnalyzingTicket,
  isChangingPotentialDuplicate = false,
  isLoadingMetadata,
  job,
  load,
  metadata,
  onAddNewTicketClick,
  onPotentialDuplicateChange,
  order,
  ticket,
  signatures,
}: DigitalTicketViewProps) {
  const { singular } = useDispatchNumberLabel();
  const { userStore } = useStores();
  const { currencyFormatter } = useCompanyCurrency();

  const shouldShowHaulerRateCard = useShouldShowHaulerRateCard();
  const stagingWaypointEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.stagingWaypoint,
  });
  // Load-grain pricing history only exists for load-level-pricing companies.
  const loadLevelPricingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.loadLevelPricing,
  });

  const canOnlySeeDriverRates = userStore.isDriver;

  const isOrderOwner = order ? userCompanyOwnsOrder(userStore, order) : false;

  const { freightRateStats, materialRateStats } = useMemo(() => {
    const formatRateValue = (type: RateType, value: number | null): string => {
      if (value === null) return '';
      if (type === RateType.RATE_COMMISSION) return `${value}%`;
      return currencyFormatter(value);
    };

    const freightStats: { title: string; content: string }[] = [];
    const materialStats: { title: string; content: string }[] = [];

    if (canOnlySeeDriverRates) {
      const driverRate = extractRateInfoFromJob(job, 'driver');
      if (driverRate) {
        const value = formatRateValue(driverRate.rateType, driverRate.rateValue);
        const typeLabel = t(`rate_types.${snakeCase(driverRate.rateType)}`);
        freightStats.push({
          title: t('order.form.driver_rate'),
          content: value ? `${value} ${typeLabel}` : typeLabel,
        });
      }

      return { freightRateStats: freightStats, materialRateStats: materialStats };
    }

    if (shouldShowHaulerRateCard) {
      const haulerRate = extractRateInfoFromJob(job, 'hauler');
      if (haulerRate) {
        const typeLabel = t(`rate_types.${snakeCase(haulerRate.rateType)}`);
        freightStats.push({
          title: t('order.form.haul_rate_card'),
          content: typeLabel,
        });
      }

      return { freightRateStats: freightStats, materialRateStats: materialStats };
    }

    if (isOrderOwner) {
      const customerRate = extractRateInfoFromJob(job, 'customer');
      if (customerRate) {
        const value = formatRateValue(customerRate.rateType, customerRate.rateValue);
        const typeLabel = t(`rate_types.${snakeCase(customerRate.rateType)}`);
        freightStats.push({
          title: t('ticket_drawer_v2.digital_ticket.customer_freight_rate'),
          content: value ? `${value} ${typeLabel}` : typeLabel,
        });
      }
    } else {
      const haulerRate = extractRateInfoFromJob(job, 'hauler');
      const haulRate = extractRateInfoFromJob(job, 'haul');
      const rate = haulerRate ?? haulRate;
      if (rate) {
        const value = formatRateValue(rate.rateType, rate.rateValue);
        const typeLabel = t(`rate_types.${snakeCase(rate.rateType)}`);
        freightStats.push({
          title: t('order.form.haul_rate_card'),
          content: value ? `${value} ${typeLabel}` : typeLabel,
        });
      }
    }

    const payRate = extractRateInfoFromJob(job, 'pay');
    if (payRate) {
      const value = formatRateValue(payRate.rateType, payRate.rateValue);
      const typeLabel = t(`rate_types.${snakeCase(payRate.rateType)}`);
      freightStats.push({
        title: t('ticket_drawer_v2.digital_ticket.vendor_freight_rate'),
        content: value ? `${value} ${typeLabel}` : typeLabel,
      });
    }

    const driverRate = extractRateInfoFromJob(job, 'driver');
    if (driverRate) {
      const value = formatRateValue(driverRate.rateType, driverRate.rateValue);
      const typeLabel = t(`rate_types.${snakeCase(driverRate.rateType)}`);
      freightStats.push({
        title: t('order.form.driver_rate'),
        content: value ? `${value} ${typeLabel}` : typeLabel,
      });
    }

    if (isOrderOwner) {
      const materialRate = load.materialRateAssignment ?? job.materialRateAssignment;
      if (materialRate) {
        const uom = materialRate.unitOfMeasure
          ? t(`rate_types.rate_per_${snakeCase(materialRate.unitOfMeasure)}`)
          : '';

        if (materialRate.sellPrice !== null) {
          materialStats.push({
            title: t('form_fields.material_rate_sell_price'),
            content: uom
              ? `${currencyFormatter(materialRate.sellPrice)} ${uom}`
              : currencyFormatter(materialRate.sellPrice),
          });
        }

        if (materialRate.buyPrice !== null) {
          materialStats.push({
            title: t('form_fields.material_rate_buy_price'),
            content: uom
              ? `${currencyFormatter(materialRate.buyPrice)} ${uom}`
              : currencyFormatter(materialRate.buyPrice),
          });
        }
      }
    }

    return { freightRateStats: freightStats, materialRateStats: materialStats };
  }, [
    job,
    load,
    currencyFormatter,
    canOnlySeeDriverRates,
    isOrderOwner,
    shouldShowHaulerRateCard,
  ]);

  const hasAnyRates = freightRateStats.length > 0 || materialRateStats.length > 0;
  const ticketTime = ticket?.serviceDate ?? job.jobStartAt;
  const createdAt = ticket?.createdAt ?? null;

  if (!ticket) {
    return (
      <TicketDetailsBox>
        <FlexColumn
          sx={{
            alignItems: 'center',
            gap: 0.5,
            height: '300px',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={(theme) => ({
              color: theme.brandV2.colors.treadBlack,
              fontSize: '14px',
              fontWeight: 600,
              mt: '44px',
              textAlign: 'center',
            })}
          >
            {t('ticket_drawer_v2.details.upload_image')}
          </Typography>

          <Typography
            sx={(theme) => ({
              color: theme.brandV2.colors.treadBlack,
              fontSize: '12px',
              textAlign: 'center',
            })}
          >
            {t('ticket_drawer_v2.details.manually_add_ticket')}
          </Typography>

          {onAddNewTicketClick && (
            <SmallButton
              color="inherit"
              onClick={onAddNewTicketClick}
              sx={(theme) => ({
                bgcolor: 'background.paper',
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                mt: 1,
              })}
            >
              {t('ticket_drawer_v2.details.type_in_ticket_data')}
            </SmallButton>
          )}
        </FlexColumn>
      </TicketDetailsBox>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {isAnalyzingTicket && (
        <Box
          sx={(theme) => ({
            alignItems: 'center',
            bgcolor:
              theme.palette.mode === 'dark'
                ? 'rgba(10, 24, 32, 0.8)'
                : 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: '100%',
            justifyContent: 'center',
            left: 0,
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 1,
          })}
        >
          <CircularProgress size={18} />

          <Typography
            variant="body2"
            sx={[
              {
                fontWeight: 500,
              },
              (theme) => ({ color: theme.brandV2.colors.treadBlue }),
            ]}
          >
            {t('ticket_drawer_v2.image_uploader.scanning_ticket_image_message')}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          opacity: isAnalyzingTicket ? 0.5 : 1,
          pointerEvents: isAnalyzingTicket ? 'none' : 'auto',
        }}
      >
        <Box>
          {ticket.potentialDuplicateDismissedAt && (
            <DismissedBanner
              badge={
                <OutlinedBadge
                  ariaLabel={t('approvals.files.ticket_marked_not_a_duplicate')}
                  dataTestId="ticket-duplicate-badge-dismissed"
                  Icon={FactCheckOutlined}
                  sx={{ backgroundColor: 'transparent' }}
                />
              }
              dataTestId="ticket-marked-not-a-duplicate"
              sx={{ mb: 2 }}
            >
              <DismissalDetails
                dismissedAt={ticket.potentialDuplicateDismissedAt}
                isUndoing={isChangingPotentialDuplicate}
                label={getDismissedDuplicateLabel(ticket)}
                onUndo={
                  onPotentialDuplicateChange
                    ? () => onPotentialDuplicateChange(true)
                    : undefined
                }
                undoDataTestId="ticket-undo-not-a-duplicate"
              />
            </DismissedBanner>
          )}
          {userStore.userCompany?.logoUrl && (
            <img
              src={userStore.userCompany.logoUrl}
              alt="Company Logo"
              style={{ maxHeight: '60px', marginBottom: '8px' }}
            />
          )}
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Typography sx={{ fontSize: TICKET_NUMBER_FONT_SIZE, fontWeight: 600 }}>
              {ticket.ticketNumber || NO_CONTENT_INDICATOR}
            </Typography>
            {ticket.isPotentialDuplicateFlagged && (
              <Chip
                color="error"
                hideDropdownArrow={true}
                label={
                  <Box
                    sx={{
                      p: 0,
                      m: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                    }}
                  >
                    <Warning sx={{ fontSize: '14px' }} />
                    {t('approvals.files.loads_table.duplicate')}
                  </Box>
                }
              />
            )}
            {ticket.isPotentialDuplicateFlagged && onPotentialDuplicateChange && (
              <OutlinedActionButton
                dataTestId="ticket-not-a-duplicate"
                isBusy={isChangingPotentialDuplicate}
                onClick={() => onPotentialDuplicateChange(false)}
              >
                {t('approvals.files.ticket_not_a_duplicate')}
              </OutlinedActionButton>
            )}
          </Box>
          <Subtext>{load.loadId || NO_CONTENT_INDICATOR}</Subtext>
        </Box>

        <Box
          sx={(theme) => ({
            columnGap: 2,
            display: 'grid',
            gridTemplateColumns: `repeat(2, calc(50% - ${theme.spacing(1)}))`,
            mb: 3,
            mt: 2,
          })}
        >
          <Stat
            title={t('ticket_drawer_v2.digital_ticket.ticket_time')}
            content={ticketTime ? dateFormat(ticketTime, TIMESTAMP_FORMAT) : null}
          />
          <Stat
            title={t('ticket_drawer_v2.digital_ticket.created')}
            content={createdAt ? dateFormat(createdAt, TIMESTAMP_FORMAT) : null}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {hasAnyRates && (
          <>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: '18px',
                  fontWeight: 600,
                })}
              >
                {t('order.form.rates')}
              </Typography>

              {loadLevelPricingEnabled && (
                <PricingHistoryButton
                  analyticsSurface="ticket_drawer"
                  buttonVariant="outlined"
                  label={t('loads.pricing.history.short')}
                  loadId={load.id}
                  subtitle={t('loads.pricing.history.load_ref', { ref: load.loadId })}
                />
              )}
            </Box>
            {freightRateStats.length > 0 && (
              <Box
                sx={(theme) => ({
                  columnGap: 2,
                  display: 'grid',
                  gridTemplateColumns: `repeat(2, calc(50% - ${theme.spacing(1)}))`,
                  mb: 3,
                  rowGap: 3,
                })}
              >
                {freightRateStats.map((stat) => (
                  <Stat key={stat.title} title={stat.title} content={stat.content} />
                ))}
              </Box>
            )}
            {materialRateStats.length > 0 && (
              <Box
                sx={(theme) => ({
                  columnGap: 2,
                  display: 'grid',
                  gridTemplateColumns: `repeat(2, calc(50% - ${theme.spacing(1)}))`,
                  mb: 3,
                  rowGap: 3,
                })}
              >
                {materialRateStats.map((stat) => (
                  <Stat key={stat.title} title={stat.title} content={stat.content} />
                ))}
              </Box>
            )}
          </>
        )}

        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: '18px',
            fontWeight: 600,
            mb: 2,
          })}
        >
          {t('ticket_drawer_v2.digital_ticket.load_info')}
        </Typography>
        <Box
          sx={(theme) => ({
            columnGap: 2,
            display: 'grid',
            gridTemplateColumns: `repeat(2, calc(50% - ${theme.spacing(1)}))`,
            mb: 3,
            rowGap: 3,
          })}
        >
          <Stat
            title={t('ticket_drawer_v2.digital_ticket.material')}
            content={ticket?.material?.name || load.material?.name}
          />
          <Stat
            title={t('ticket_drawer_v2.digital_ticket.quantity')}
            content={
              ticket?.quantity
                ? `${numberToIntegerOrDecimal(Number(ticket.quantity))}${ticket.unitOfMeasure?.name ? ` ${ticket.unitOfMeasure.name}` : ''}`
                : null
            }
          />
          <Stat title={t('project.form.project_phase_name')} content={load.phase?.name} />
          <Stat title={t('project.form.project_phase_code')} content={load.phase?.code} />
        </Box>

        {signatures && signatures.length > 0 && (
          <>
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadBlack,
                fontSize: '18px',
                fontWeight: 600,
                mb: 2,
              })}
            >
              {t('ticket_drawer_v2.digital_ticket.signatures')}
            </Typography>

            <SignaturesViewer signatures={signatures} sx={{ mb: 3 }} />
          </>
        )}

        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: '18px',
            fontWeight: 600,
            mb: 2,
          })}
        >
          {t('ticket_drawer_v2.digital_ticket.summary')}
        </Typography>
        <Box
          sx={(theme) => ({
            columnGap: 2,
            display: 'grid',
            gridTemplateColumns: `repeat(2, calc(50% - ${theme.spacing(1)}))`,
            mb: 3,
            rowGap: 3,
          })}
        >
          <Stat
            title={t('ticket_drawer_v2.digital_ticket.customer')}
            content={job.order?.account?.name}
          />
          <Stat
            title={t('ticket_drawer_v2.digital_ticket.project')}
            content={job.project?.name}
          />
          <Stat
            title={singular('ticket_drawer_v2.digital_ticket.dispatch_number')}
            content={job.order?.dispatchNumber?.name}
          />
          <Box>
            <Stat
              title={t('ticket_drawer_v2.digital_ticket.pickup_site')}
              content={load.pickUpWaypoints[0]?.site?.name}
            />
            {load.pickUpWaypoints[0]?.site?.fullAddress && (
              <Subtext>{load.pickUpWaypoints[0].site.fullAddress}</Subtext>
            )}
          </Box>
          <Box>
            <Stat
              title={t('ticket_drawer_v2.digital_ticket.dropoff_site')}
              content={load.dropOffWaypoints[0]?.site?.name}
            />
            {load.dropOffWaypoints[0]?.site?.fullAddress && (
              <Subtext>{load.dropOffWaypoints[0].site.fullAddress}</Subtext>
            )}
          </Box>
          {stagingWaypointEnabled && load.stagingWaypoints[0]?.site && (
            <Box>
              <Stat
                title={t('ticket_drawer_v2.digital_ticket.staging_site')}
                content={load.stagingWaypoints[0]?.site?.name}
              />
              {load.stagingWaypoints[0]?.site?.fullAddress && (
                <Subtext>{load.stagingWaypoints[0].site.fullAddress}</Subtext>
              )}
            </Box>
          )}
        </Box>

        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: '18px',
            fontWeight: 600,
            mb: 2,
          })}
        >
          {t('ticket_drawer_v2.digital_ticket.delivered')}
        </Typography>
        <Box
          sx={(theme) => ({
            columnGap: 2,
            display: 'grid',
            gridTemplateColumns: `repeat(2, calc(50% - ${theme.spacing(1)}))`,
            mb: 3,
            rowGap: 3,
          })}
        >
          <Stat
            title={t('ticket_drawer_v2.digital_ticket.driver')}
            content={
              job.driverFullName ||
              (job.driver
                ? `${job.driver.firstName || ''} ${job.driver.lastName || ''}`.trim()
                : undefined)
            }
          />
          <Stat
            title={t('ticket_drawer_v2.digital_ticket.vendor')}
            content={job.vendorJobAssignment?.vendorAccount?.name}
          />
          <Stat
            title={t('ticket_drawer_v2.digital_ticket.equipment')}
            content={job.equipment?.name}
          />
          <Stat
            title={t('ticket_drawer_v2.digital_ticket.license_plate')}
            content={job.equipment?.licenseNumber}
          />
        </Box>

        {load.loadSummary && (
          <>
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadBlack,
                fontSize: '18px',
                fontWeight: 600,
                mb: 2,
              })}
            >
              {t('ticket_drawer_v2.digital_ticket.times')}
            </Typography>
            <Box
              sx={(theme) => ({
                columnGap: 2,
                display: 'grid',
                gridTemplateColumns: `repeat(2, calc(50% - ${theme.spacing(1)}))`,
                mb: 3,
                rowGap: 3,
              })}
            >
              {[
                {
                  datetime: load.loadSummary.arrivedPickupAt,
                  titleKey: 'arrived_at_pickup',
                },
                { datetime: load.loadSummary.loadedAt, titleKey: 'loaded' },
                {
                  datetime: load.loadSummary.arrivedDropoffAt,
                  titleKey: 'arrived_at_dropoff',
                },
                { datetime: load.loadSummary.unloadedAt, titleKey: 'unloaded' },
              ].map(({ datetime, titleKey }) => (
                <Stat
                  key={titleKey}
                  title={t(`loads.${titleKey}`)}
                  content={datetime ? <StampTime datetime={datetime} /> : null}
                />
              ))}
            </Box>
          </>
        )}

        {load.notes && (
          <>
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadBlack,
                fontSize: '18px',
                fontWeight: 600,
                mb: 2,
              })}
            >
              {t('approvals.ticket_details.notes')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
              {load.notes}
            </Typography>
          </>
        )}

        {metadata && !isLoadingMetadata && (
          <>
            <Divider sx={{ mt: 4 }} />
            <TicketMetadataSummary metadata={metadata} />
          </>
        )}
      </Box>
    </Box>
  );
}

interface StatProps {
  content?: React.ReactNode;
  title: string;
}

function Subtext({ children }: { children: string }) {
  return (
    <Typography
      sx={(theme) => ({
        color: theme.brandV2.colors.treadGray2,
        fontSize: STAT_SUBTEXT_FONT_SIZE,
        mt: 0.5,
      })}
    >
      {children}
    </Typography>
  );
}

interface StampTimeProps {
  datetime: Dayjs;
}

function StampTime({ datetime }: StampTimeProps) {
  return (
    <>
      <Typography sx={{ fontSize: STAT_VALUE_FONT_SIZE }}>
        {dateFormat(datetime, 'h:mm A')}
      </Typography>
      <Subtext>{dateFormat(datetime)}</Subtext>
    </>
  );
}

function Stat({ content, title }: StatProps) {
  const actualContent =
    content && (typeof content !== 'string' || content.trim() !== '')
      ? content
      : NO_CONTENT_INDICATOR;
  const isString = typeof actualContent === 'string';

  return (
    <FlexColumn sx={{ gap: 0 }}>
      <Typography sx={{ fontSize: STAT_LABEL_FONT_SIZE, fontWeight: 600 }}>
        {title}
      </Typography>
      {isString ? (
        <Typography sx={{ fontSize: STAT_VALUE_FONT_SIZE }}>{actualContent}</Typography>
      ) : (
        actualContent
      )}
    </FlexColumn>
  );
}
